import React from "react";
import UserBased from "./UserBased";
import { useUser } from "src/shared/context/UserContext";
import AdminOwnerBased from "./AdminOwnerBased";

export interface ApprovalContact {
  name: string;
  email: string;
  role: string;
}

export type ApprovalResult = { type: "approve" } | { type: "increase-credit"; newLimit: number } | { type: "contacts"; selectedContacts: ApprovalContact[] };

export interface OverCreditPermissionProps {
  approvalContacts: ApprovalContact[];
  currentCreditLimit?: number;
  onConfirm: (result: ApprovalResult) => void;
  onCancel: () => void;
  loading?: boolean;
  success?: boolean;
  selectedContacts?: ApprovalContact[];
}

function OverCreditPermission({ approvalContacts, currentCreditLimit, onConfirm, onCancel, loading = false, success = false, selectedContacts = [] }: OverCreditPermissionProps) {
  const { user } = useUser();
  const workBased = user?.role[0] === "User" ? true : false;

  // ✅ Success popup shown for 3s before parent closes
  if (success) {
    const isPlural = selectedContacts.length > 1;

    return (
      <div className="max-w-md w-full bg-white border border-gray-200 rounded-xl shadow-sm p-8 flex flex-col items-center text-center animate-[fadeIn_0.3s_ease-out]">
        {/* Animated check icon */}
        <div className="relative w-16 h-16 rounded-full bg-green-50 flex items-center justify-center mb-5">
          <span className="absolute inset-0 rounded-full bg-green-100 animate-ping opacity-30" />
          <div className="relative w-12 h-12 rounded-full bg-green-500 flex items-center justify-center shadow-md shadow-green-200">
            <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round">
              <path d="M4.5 12.75l6 6 9-13.5" />
            </svg>
          </div>
        </div>

        {/* Heading */}
        <h3 className="text-lg font-semibold text-gray-900">Approval Request Sent</h3>

        {/* Description */}
        <p className="text-sm text-gray-500 mt-2 leading-relaxed max-w-xs">
          We've notified the selected approver{isPlural ? "s" : ""}. You'll be able to proceed once {isPlural ? "they respond" : "they respond"}.
        </p>

        {/* Selected contact chips */}
        {selectedContacts.length > 0 && (
          <div className="flex flex-wrap justify-center gap-2 mt-4">
            {selectedContacts.map((c, i) => (
              <span key={i} className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-600 bg-gray-100 px-2.5 py-1 rounded-full">
                <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                {c.name}
              </span>
            ))}
          </div>
        )}

        {/* Progress hint */}
        <div className="w-full mt-6">
          <div className="h-1 w-full bg-gray-100 rounded-full overflow-hidden">
            <div className="h-full bg-green-500 rounded-full animate-[progress_3s_linear_forwards]" />
          </div>
          <p className="text-xs text-gray-400 mt-2">Closing automatically in a moment…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-md w-full bg-white border border-gray-200 rounded-xl shadow-sm p-6">
      {workBased ? (
        <UserBased approvalContacts={approvalContacts} onConfirm={onConfirm} onCancel={onCancel} loading={loading} />
      ) : (
        <AdminOwnerBased currentLimit={currentCreditLimit} onConfirm={onConfirm} onCancel={onCancel} loading={loading} />
      )}
    </div>
  );
}

export default OverCreditPermission;
